import { Badge, Box, Button, Card, Flex, Progress, Text } from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { useRegressionList, useRegressionRun } from "./api";
import { RegressionTestRow } from "./types";
import { TestDetailDialog } from "./TestDetailDialog";
import { RunSidebar } from "./RunSidebar";
import { formatDuration, formatRunDate } from "./format";

export function AuiRegressionTestsV2() {
  const navigate = useNavigate();
  const { data: listData, isLoading: listLoading } = useRegressionList();
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<RegressionTestRow | null>(
    null
  );

  const effectiveDatasetId = useMemo(() => {
    if (selectedDatasetId) return selectedDatasetId;
    return listData?.suites[0]?.id ?? null;
  }, [selectedDatasetId, listData]);

  const effectiveRunId = useMemo(() => {
    if (selectedRunId) return selectedRunId;
    if (!listData || !effectiveDatasetId) return null;
    const suite = listData.suites.find((s) => s.id === effectiveDatasetId);
    return suite?.runs.find((r) => r.hasResults)?.id ?? null;
  }, [selectedRunId, listData, effectiveDatasetId]);

  const datasetName = useMemo(() => {
    if (!listData || !effectiveDatasetId) return undefined;
    return listData.suites.find((s) => s.id === effectiveDatasetId)?.name;
  }, [listData, effectiveDatasetId]);

  const runStartedAt = useMemo(() => {
    if (!listData || !effectiveDatasetId || !effectiveRunId) return undefined;
    const suite = listData.suites.find((s) => s.id === effectiveDatasetId);
    return suite?.runs.find((r) => r.id === effectiveRunId)?.startedAt;
  }, [listData, effectiveDatasetId, effectiveRunId]);

  const { data: runData, isLoading: runLoading } = useRegressionRun(
    effectiveDatasetId,
    effectiveRunId,
    datasetName
  );

  const handleSelectRun = useCallback(
    (datasetId: string, runId: string) => {
      setSelectedDatasetId(datasetId);
      setSelectedRunId(runId);
      setSelectedTest(null);
    },
    []
  );

  const handleRowClick = useCallback((row: RegressionTestRow) => {
    setSelectedTest(row);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedTest(null);
  }, []);

  const handleBack = useCallback(() => {
    navigate("/automation-center/aui-regression-tests");
  }, [navigate]);

  const aggregate = useMemo(() => {
    if (!runData?.tests?.length) {
      return { pass: 0, fail: 0, escalated: 0, avgScore: 0, avgDuration: 0 };
    }
    const tests = runData.tests;
    const pass = tests.filter((t) => (t.overallScore ?? 0) >= 100).length;
    const fail = tests.length - pass;
    const escalated = tests.filter((t) => t.escalated).length;
    const avgScore =
      tests.reduce((acc, t) => acc + (t.overallScore ?? 0), 0) / tests.length;
    const avgDuration =
      tests.reduce((acc, t) => acc + (t.duration ?? 0), 0) / tests.length;
    return { pass, fail, escalated, avgScore, avgDuration };
  }, [runData]);

  const columns = useMemo<ColumnDef<RegressionTestRow>[]>(
    () => [
      {
        id: "testName",
        accessorKey: "testName",
        header: "Test",
        cell: ({ getValue }) => (
          <Text size="2" weight="medium">
            {String(getValue())}
          </Text>
        ),
      },
      {
        id: "overallScore",
        accessorKey: "overallScore",
        header: "Score",
        cell: ({ getValue }) => {
          const score = Number(getValue() ?? 0);
          const color = score >= 100 ? "green" : score >= 75 ? "amber" : "red";
          return (
            <Flex align="center" gap="2" style={{ minWidth: 120 }}>
              <Box style={{ flex: 1 }}>
                <Progress value={score} color={color} size="1" />
              </Box>
              <Text size="1" weight="medium" color={color}>
                {score}%
              </Text>
            </Flex>
          );
        },
      },
      {
        id: "passRate",
        accessorKey: "passRate",
        header: "Pass rate",
        cell: ({ getValue }) => (
          <Badge color="gray" variant="soft" size="1">
            {String(getValue() ?? "-")}
          </Badge>
        ),
      },
      {
        id: "duration",
        accessorKey: "duration",
        header: "Duration",
        cell: ({ getValue }) => (
          <Text size="2" color="gray">
            {formatDuration(Number(getValue() ?? 0))}
          </Text>
        ),
      },
      {
        id: "escalated",
        accessorKey: "escalated",
        header: "Escalated",
        cell: ({ row }) => {
          const t = row.original;
          if (!t.escalated) {
            return (
              <Badge color="gray" variant="soft" size="1">
                No
              </Badge>
            );
          }
          return (
            <Flex align="center" gap="1">
              <Badge
                color={t.escalatedDueToError ? "red" : "amber"}
                variant="soft"
                size="1"
              >
                {t.escalatedDueToError ? "Error" : `Turn ${t.escalatedAtTurn ?? "?"}`}
              </Badge>
            </Flex>
          );
        },
      },
      {
        id: "turns",
        header: "Turns",
        accessorFn: (row) => row.conversation?.length ?? 0,
        cell: ({ getValue }) => (
          <Text size="2" color="gray">
            {String(getValue())}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Flex
      direction="row"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <RunSidebar
        listData={listData}
        loading={listLoading}
        selectedDatasetId={effectiveDatasetId}
        selectedRunId={effectiveRunId}
        onSelect={handleSelectRun}
      />
      <Flex
        direction="column"
        gap="5"
        px="6"
        py="6"
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Flex direction="row" gap="2" justify="between" align="center">
          <Flex align="center" gap="2">
            <Button variant="ghost" size="1" onClick={handleBack}>
              <ArrowLeftIcon />
            </Button>
            <Flex direction="column" gap="1">
              <Text size="5" weight="medium">
                {runData?.suiteName ?? datasetName ?? "Simulation tests"}
              </Text>
              <Text size="2" color="gray">
                {runStartedAt
                  ? formatRunDate(runStartedAt)
                  : effectiveRunId ?? "Select a run"}
                {runData?.status ? ` · ${runData.status}` : ""}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex gap="3" wrap="wrap">
          <StatCard
            label="Tests"
            value={String(runData?.tests?.length ?? 0)}
            color="gray"
          />
          <StatCard
            label="Passed"
            value={String(aggregate.pass)}
            color="green"
            icon={<CheckCircledIcon />}
          />
          <StatCard
            label="Failed"
            value={String(aggregate.fail)}
            color="red"
            icon={<CrossCircledIcon />}
          />
          <StatCard
            label="Escalated"
            value={String(aggregate.escalated)}
            color="amber"
            icon={<ExclamationTriangleIcon />}
          />
          <StatCard
            label="Avg score"
            value={`${aggregate.avgScore.toFixed(0)}%`}
            color={
              aggregate.avgScore >= 90
                ? "green"
                : aggregate.avgScore >= 70
                ? "amber"
                : "red"
            }
          />
          <StatCard
            label="Avg duration"
            value={formatDuration(aggregate.avgDuration)}
            color="gray"
          />
        </Flex>

        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            border: "1px solid var(--gray-5)",
            borderRadius: 8,
          }}
        >
          <SimpleTable
            data={runData?.tests ?? []}
            columns={columns}
            loading={runLoading}
            onRowClicked={handleRowClick}
            getRowId={(row) => row.taskId}
          />
        </Box>

        {selectedTest ? (
          <TestDetailDialog
            open={!!selectedTest}
            onClose={handleCloseDialog}
            test={selectedTest}
          />
        ) : null}
      </Flex>
    </Flex>
  );
}

function StatCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: string;
  color: "green" | "red" | "amber" | "gray";
  icon?: React.ReactNode;
}) {
  return (
    <Card size="2" style={{ minWidth: 140 }}>
      <Flex direction="column" gap="1">
        <Flex align="center" gap="1">
          {icon ? (
            <Text size="1" color={color}>
              {icon}
            </Text>
          ) : null}
          <Text size="1" color="gray">
            {label}
          </Text>
        </Flex>
        <Text size="6" weight="bold" color={color}>
          {value}
        </Text>
      </Flex>
    </Card>
  );
}
